import { useState, type FormEvent } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  BarChart2,
  CheckCircle2,
  Download,
  FileText,
  Loader2,
  RefreshCw,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';

import { useIrrbbReport, downloadIrrbbXbrl } from './api';
import type {
  IrrbbKrdEntry,
  IrrbbScenarioResult,
} from './types';

// ── helpers ──────────────────────────────────────────────────────────────────

function fmt(v?: string | null, decimals = 2): string {
  if (v == null) return '—';
  const n = parseFloat(v);
  if (isNaN(n)) return v;
  return new Intl.NumberFormat('fr-MA', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(n);
}

function fmtPct(v?: string | null, decimals = 2): string {
  if (v == null) return '—';
  const n = parseFloat(v);
  if (isNaN(n)) return v;
  return (n * 100).toFixed(decimals) + '%';
}

function fmtCcy(v?: string | null): string {
  if (v == null) return '—';
  const n = parseFloat(v);
  if (isNaN(n)) return v;
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return (n / 1_000_000).toFixed(2) + ' M';
  if (abs >= 1_000)     return (n / 1_000).toFixed(2) + ' K';
  return fmt(v);
}

function signedClass(v?: string | null): string {
  if (v == null) return '';
  const n = parseFloat(v);
  if (isNaN(n)) return '';
  return n < 0 ? 'text-red-600' : n > 0 ? 'text-emerald-600' : '';
}

// ── sub-components ────────────────────────────────────────────────────────────

function EveScenariosTable({ scenarios }: { scenarios: IrrbbScenarioResult[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-xs text-muted-foreground uppercase tracking-wide">
            <th className="py-2 text-left pr-4">Scenario</th>
            <th className="py-2 text-right pr-4">Shock (bp)</th>
            <th className="py-2 text-right pr-4">ΔEVE (CCY)</th>
            <th className="py-2 text-right">ΔEVE / |PV|</th>
          </tr>
        </thead>
        <tbody>
          {scenarios.map((s) => (
            <tr key={s.scenario} className="border-b last:border-0 hover:bg-muted/30">
              <td className="py-2 pr-4 font-medium">{s.scenarioLabel}</td>
              <td className={`py-2 pr-4 text-right tabular-nums ${s.shockBp < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                {s.shockBp > 0 ? `+${s.shockBp}` : s.shockBp}
              </td>
              <td className={`py-2 pr-4 text-right tabular-nums ${signedClass(s.eve)}`}>
                {fmtCcy(s.eve)}
              </td>
              <td className={`py-2 text-right tabular-nums ${signedClass(s.evePct)}`}>
                {fmtPct(s.evePct, 2)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function KrdTable({ buckets, totalDv01 }: { buckets: IrrbbKrdEntry[]; totalDv01: string }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-xs text-muted-foreground uppercase tracking-wide">
            <th className="py-2 text-left pr-4">Tenor</th>
            <th className="py-2 text-right pr-4">Upper bound (days)</th>
            <th className="py-2 text-right">DV01 (CCY / 1bp)</th>
          </tr>
        </thead>
        <tbody>
          {buckets.map((b) => (
            <tr key={b.tenorLabel} className="border-b hover:bg-muted/30">
              <td className="py-2 pr-4 font-medium">{b.tenorLabel}</td>
              <td className="py-2 pr-4 text-right tabular-nums text-muted-foreground">{b.tenorDays}</td>
              <td className={`py-2 text-right tabular-nums ${signedClass(b.dv01)}`}>
                {fmtCcy(b.dv01)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t bg-muted/20 font-semibold">
            <td className="py-2 pr-4" colSpan={2}>Total DV01</td>
            <td className={`py-2 text-right tabular-nums ${signedClass(totalDv01)}`}>
              {fmtCcy(totalDv01)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ── main page ─────────────────────────────────────────────────────────────────

export default function IrrbbReportPage() {
  const [dateInput, setDateInput] = useState('');
  const [activeDate, setActiveDate] = useState<string | undefined>(undefined);
  const [xbrlLoading, setXbrlLoading] = useState(false);
  const [xbrlError,   setXbrlError]   = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch } = useIrrbbReport(activeDate);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setActiveDate(dateInput.trim() || undefined);
  }

  async function handleXbrlDownload() {
    setXbrlLoading(true);
    setXbrlError(null);
    try {
      await downloadIrrbbXbrl(activeDate);
    } catch (err) {
      setXbrlError((err as Error).message ?? 'Failed to download XBRL export.');
    } finally {
      setXbrlLoading(false);
    }
  }

  const report = data;
  const meta   = report?.metadata;
  // Download is only meaningful when EVE section is present
  const canExport = Boolean(report?.eveSection);

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="h-6 w-6" />
            IRRBB Regulatory Report
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            BAM · BCBS 368 · Interest Rate Risk in the Banking Book
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={handleXbrlDownload}
            disabled={!canExport || xbrlLoading}
            title={canExport ? 'Download XBRL instance document for BAM submission' : 'Load a report with EVE data first'}
          >
            {xbrlLoading
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : <Download className="h-4 w-4 mr-2" />}
            Download XBRL
          </Button>
        </div>
      </div>

      {/* ── Date selector ── */}
      <Card>
        <CardContent className="pt-4">
          <form onSubmit={handleSubmit} className="flex items-center gap-3">
            <label className="text-sm font-medium text-muted-foreground">Reporting date</label>
            <input
              type="date"
              value={dateInput}
              onChange={(e) => setDateInput(e.target.value)}
              className="border rounded px-2 py-1 text-sm bg-background"
            />
            <Button type="submit" size="sm" variant="secondary">
              Load
            </Button>
            {activeDate && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => { setDateInput(''); setActiveDate(undefined); }}
              >
                Use latest
              </Button>
            )}
          </form>
          {!activeDate && (
            <p className="text-xs text-muted-foreground mt-2">
              No date selected — showing the most recent EVE result.
            </p>
          )}
        </CardContent>
      </Card>

      {/* ── Loading / error states ── */}
      {isLoading && (
        <div className="flex items-center justify-center py-16 text-muted-foreground gap-3">
          <Loader2 className="h-6 w-6 animate-spin" />
          Assembling IRRBB report…
        </div>
      )}

      {isError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {(error as { message?: string })?.message ?? 'Failed to load IRRBB report.'}
          </AlertDescription>
        </Alert>
      )}

      {xbrlError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            XBRL export failed: {xbrlError}
          </AlertDescription>
        </Alert>
      )}

      {report && meta && (
        <>
          {/* ── Metadata card ── */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Report Metadata</CardTitle>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="font-mono text-xs">
                    {meta.reportingFramework}
                  </Badge>
                  {meta.partial ? (
                    <Badge variant="destructive" className="text-xs flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" /> Partial
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="text-xs flex items-center gap-1 bg-emerald-100 text-emerald-800">
                      <CheckCircle2 className="h-3 w-3" /> Complete
                    </Badge>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground text-xs uppercase tracking-wide">Report ID</p>
                <p className="font-mono text-xs truncate">{meta.reportId}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs uppercase tracking-wide">Reporting Date</p>
                <p className="font-semibold">{meta.reportingDate ?? '—'}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs uppercase tracking-wide">Generated At</p>
                <p className="text-xs">{new Date(meta.generatedAt).toLocaleString('fr-MA')}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs uppercase tracking-wide">Engine</p>
                <p className="font-mono text-xs">{meta.engineVersion}</p>
              </div>
            </CardContent>
            {meta.partial && (
              <div className="px-6 pb-4">
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription className="text-xs">
                    One or more sections could not be populated because NIM or Duration results
                    are missing for this reporting date. Run the missing calculations and
                    reload the report.
                  </AlertDescription>
                </Alert>
              </div>
            )}
          </Card>

          {/* ── EVE Section ── */}
          {report.eveSection ? (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">EVE — Economic Value of Equity</CardTitle>
                    <CardDescription>
                      Six Basel scenarios · Portfolio PV&nbsp;
                      <span className="font-semibold text-foreground">
                        {fmtCcy(report.eveSection.portfolioPv)} CCY
                      </span>
                    </CardDescription>
                  </div>
                  {report.eveSection.exceedsThreshold ? (
                    <Badge variant="destructive" className="flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Outlier &gt; 20%
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="bg-emerald-100 text-emerald-800 flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" />
                      Within threshold
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <EveScenariosTable scenarios={report.eveSection.scenarios} />

                {/* Worst case summary */}
                <div className="border rounded-lg p-4 bg-muted/20 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground text-xs uppercase tracking-wide">Worst Scenario</p>
                    <p className="font-semibold">{report.eveSection.worstCase.scenarioLabel}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs uppercase tracking-wide">|ΔEVE max|</p>
                    <p className={`font-semibold tabular-nums ${signedClass(report.eveSection.worstCase.eve)}`}>
                      {fmtCcy(report.eveSection.worstCase.eve)}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs uppercase tracking-wide">ΔEVE / |PV|</p>
                    <p className={`font-semibold tabular-nums ${signedClass(report.eveSection.worstCaseEvePct)}`}>
                      {fmtPct(report.eveSection.worstCaseEvePct, 2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs uppercase tracking-wide">Threshold (BCBS §99)</p>
                    <p className="font-semibold tabular-nums">
                      {fmtPct(report.eveSection.supervisoryThresholdPct, 0)}
                    </p>
                  </div>
                </div>

                {report.eveSection.exceedsThreshold && meta.thresholdNote && (
                  <Alert>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription className="text-xs">{meta.thresholdNote}</AlertDescription>
                  </Alert>
                )}

                {report.eveSection.degradedMode && (
                  <p className="text-xs text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    EVE calculated in degraded mode — yield curve unavailable, flat rate used.
                  </p>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="border-dashed">
              <CardContent className="py-8 text-center text-muted-foreground text-sm">
                No EVE result for this reporting date. Run an EVE calculation first.
              </CardContent>
            </Card>
          )}

          {/* ── NIM + Duration row ── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

            {/* NIM */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">NIM — Net Interest Margin</CardTitle>
                <CardDescription>
                  {report.nimSection
                    ? `${report.nimSection.horizonMonths}-month horizon · Scenario: ${report.nimSection.scenarioCode}`
                    : 'Not available for this date'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {report.nimSection ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div className="text-center">
                        <p className="text-muted-foreground text-xs">Baseline</p>
                        <p className="text-lg font-bold tabular-nums">
                          {fmtPct(report.nimSection.nimBaseline, 3)}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-muted-foreground text-xs">Stressed</p>
                        <p className="text-lg font-bold tabular-nums">
                          {fmtPct(report.nimSection.nimStressed, 3)}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-muted-foreground text-xs">Delta</p>
                        <p className={`text-lg font-bold tabular-nums ${signedClass(report.nimSection.nimDelta)}`}>
                          {fmtPct(report.nimSection.nimDelta, 3)}
                        </p>
                      </div>
                    </div>
                    {report.nimSection.degradedMode && (
                      <p className="text-xs text-amber-600 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" /> Degraded mode — flat rate used.
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground py-4 text-center">
                    No NIM result for this reporting date.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Duration */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Duration &amp; DV01</CardTitle>
                <CardDescription>
                  {report.durationSection
                    ? `Scenario: ${report.durationSection.scenarioCode}`
                    : 'Not available for this date'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {report.durationSection ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                      <div>
                        <p className="text-xs text-muted-foreground">Modified Duration</p>
                        <p className="font-semibold tabular-nums">
                          {fmt(report.durationSection.modifiedDuration, 4)} yr
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">DV01</p>
                        <p className={`font-semibold tabular-nums ${signedClass(report.durationSection.dv01)}`}>
                          {fmtCcy(report.durationSection.dv01)} CCY
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Stressed Duration</p>
                        <p className="font-semibold tabular-nums">
                          {fmt(report.durationSection.stressedDuration, 4)} yr
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Stressed DV01</p>
                        <p className={`font-semibold tabular-nums ${signedClass(report.durationSection.stressedDv01)}`}>
                          {fmtCcy(report.durationSection.stressedDv01)} CCY
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">PV Shift</p>
                        <p className={`font-semibold tabular-nums ${signedClass(report.durationSection.pvShift)}`}>
                          {fmtCcy(report.durationSection.pvShift)} CCY
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Δ Duration</p>
                        <p className={`font-semibold tabular-nums ${signedClass(report.durationSection.durationDelta)}`}>
                          {fmt(report.durationSection.durationDelta, 4)} yr
                        </p>
                      </div>
                    </div>
                    {report.durationSection.degradedMode && (
                      <p className="text-xs text-amber-600 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" /> Degraded mode — flat rate used.
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground py-4 text-center">
                    No Duration result for this reporting date.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* ── KRD Section ── */}
          {report.krdSection && report.krdSection.buckets.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <BarChart2 className="h-4 w-4" />
                  Key Rate Duration (KRD)
                </CardTitle>
                <CardDescription>
                  DV01 per tenor band · 10 standard IRRBB buckets
                </CardDescription>
              </CardHeader>
              <CardContent>
                <KrdTable
                  buckets={report.krdSection.buckets}
                  totalDv01={report.krdSection.totalDv01}
                />
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* ── Empty state (before first load) ── */}
      {!isLoading && !isError && !report && (
        <div className="text-center py-16 text-muted-foreground">
          <FileText className="h-12 w-12 mx-auto mb-4 opacity-30" />
          <p className="text-sm">Select a reporting date or click Refresh to load the latest report.</p>
        </div>
      )}
    </div>
  );
}
